/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState, useEffect } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Spinner, useToast } from '@chakra-ui/react'
import AIMetadataDataLists from './components/AIMetadataDataLists'
import AIMetadataCompleteDataList from './components/AIMetadataCompleteDataList'
import { aiMetadataBatchesUrls } from '@/configs/urls'
import HeaderBatchItem from '@/components/batches/HeaderBatchItem'
import ProcessBatch from '@/components/batches/ProcessBatch'
import { columnsTable, getTableValues } from './handlers'
import ModalBodyProcessBatch from './components/ModalBodyProcessBatch'
import MainTotalItems from '@/components/listings/MainTotalItems'
import {
  AI_METADATA_BATCH_TITLE,
  BUILD_MULTIPLE_AI_METADATA_DESCRIPTION,
  AI_METADATA_QUEUED_FOR_BUILDING_TITLE,
  AI_METADATA_BATCH,
  ACCEPT_TERMS_AI_METADATA,
  CREATE_AI_METADATA
} from '@/configs/strings/ai-metadata-batches.strings'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import {
  getAIMetadataBuildBatch,
  getAIMetadataBuildBatchItemsAsCsv,
  getAIMetadataBuildBatchItemsByBatchId,
  startAIMetadataBuildBatchProcessing
} from '@/services/ai-metadata-build-batches'
import { useAuth0 } from '@auth0/auth0-react'
import { getEmbeddingModels } from '@/services/embedding-models'
import DefaultAlert from '@/components/alerts/DefaultAlert'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import DownloadFileButton from '@/components/buttons/DownloadFileButton'
import { EXPORT_AI_METADATA_LABEL } from '@/configs/strings/ai-metadata.strings'
import { generalBatchStates } from '@/configs/server-enums'
import { defaultToastConfig } from '@/configs/toast'
import { getBatchProcessInfo } from '@/utils/batches'
import { chakraAlertStatus } from '@/configs/chakra-ui/alert-props'

const AIMetadataBatch = () => {
  const toast = useToast()
  const [hasAlert, setHasAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(chakraAlertStatus.error)
  const [inProgress, setInProgress] = useState(false)
  const [alert, setAlert] = useState()
  const { batchId } = useParams()
  const navigate = useNavigate()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const { processing } = generalBatchStates
  const {
    itemsResponse: aiMetadataBatch,
    loading: isLoading,
    reloadItems: reloadBatch
  } = useFetchItemsWithParamsV2({
    fetchService: getAIMetadataBuildBatch,
    fetchParams: { id: batchId }
  })
  const {
    itemsResponse,
    loading: isLoadingItems,
    onChangePage,
    page,
    reloadItems,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getAIMetadataBuildBatchItemsByBatchId,
    fetchParams: { id: batchId }
  })
  const {
    itemsResponse: aiStrategies,
    loading: isLoadingAIStrategies
  } = useFetchItemsWithParamsV2({ fetchService: getEmbeddingModels })
  const alertNoStrategy = 'All AI metadata must have an AI strategy; each AI metadata needs to be edited to add a strategy.'

  const setMissingStrategies = (isEmptyStrategy) => {
    if (isEmptyStrategy) {
      setHasAlert(true)
      setAlert({ code: alertNoStrategy })
      setAlertStatus(chakraAlertStatus.warning)
    } else {
      setHasAlert(false)
      setAlert(null)
      setAlertStatus(chakraAlertStatus.error)
    }
  }

  const onStartProcess = async (data, closeModal) => {
    try {
      const isCompleted = aiMetadataBatch.status === generalBatchStates.completed || aiMetadataBatch.status === generalBatchStates.completedWithFails
      const isEmptyStrategy = !aiMetadataBatch?.processingCostDetails?.averageCostAiMetadata && !isCompleted

      if (isEmptyStrategy) {
        setMissingStrategies(isEmptyStrategy)

        return null
      }

      setInProgress(true)
      setHasAlert(false)
      setAlert(null)
      closeModal()
      const aiMetadataBatchUpdate = await startAIMetadataBuildBatchProcessing({ id: batchId, data }, getAccessToken)

      if (aiMetadataBatchUpdate.state === processing) {
        toast({
          title: AI_METADATA_BATCH,
          description: `The ${AI_METADATA_BATCH} '${aiMetadataBatchUpdate.name}' is being processed; we will notify you when it is finished.`,
          status: 'success',
          ...defaultToastConfig
        })
      }
    } catch (error) {
      toast({
        title: `Error on ${AI_METADATA_BATCH}`,
        description: error.response?.data?.message ||
          `Failed processing the ${AI_METADATA_BATCH}. Please contact with us.`,
        status: 'error',
        ...defaultToastConfig
      })
      setInProgress(false)
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
    } finally {
      navigate(`/${aiMetadataBatchesUrls.base}`, { replace: true })
    }
  }

  const {
    batchHasErrors,
    batchState,
    isCompleted,
    isHiddenForm
  } = getBatchProcessInfo(aiMetadataBatch?.state)
  const batchName = aiMetadataBatch?.name

  const HeaderBatch = () => (
    <HeaderBatchItem
      title={batchName}
      batchState={batchState}
    />
  )

  const { averageCostAiMetadata, ...rowTable } = getTableValues({
    averageCostAiMetadata: aiMetadataBatch?.processingCostDetails?.averageCostAiMetadata,
    embeddingModels: aiMetadataBatch?.processingCostDetails?.embeddingModels,
    processingCost: +aiMetadataBatch?.processingCost,
    totalLicensedAsset: aiMetadataBatch?.processingCostDetails?.totalLicensedAsset
  })

  const reloadAll = () => {
    reloadItems()
    reloadBatch()
  }

  useEffect(() => {
    const isEmptyStrategy = !aiMetadataBatch?.processingCostDetails?.averageCostAiMetadata && !isCompleted

    setMissingStrategies(isEmptyStrategy)
  }, [aiMetadataBatch, itemsResponse])

  return (
    <RegularTemplate
      childrenHeader={HeaderBatch}
      description={BUILD_MULTIPLE_AI_METADATA_DESCRIPTION}
      loadingContent={(isLoading && !aiMetadataBatch) || inProgress}
      title={AI_METADATA_BATCH_TITLE}
    >
      { !isLoading &&
        <>
          { isLoadingItems || isLoadingAIStrategies
            ? <div className="text-center">
                <Spinner size="lg" />
              </div>
            : <div>
                { hasAlert &&
                  <DefaultAlert
                    status={alertStatus}
                    message={alert?.code}
                  />
                }
                <ProcessBatch
                  alertMessage={alert?.code}
                  alertStatus="error"
                  bodyModal={
                    () => <ModalBodyProcessBatch
                      costPerAsset={averageCostAiMetadata}
                      totalAssets={rowTable.numberAssets}
                      totalCost={rowTable.aiStrategiesTotalCost}
                    />
                  }
                  columnsTable={columnsTable}
                  hasAlert={hasAlert}
                  isHiddenContinueButton={isHiddenForm}
                  labelContinue={ACCEPT_TERMS_AI_METADATA}
                  labelModalOnProcess={CREATE_AI_METADATA}
                  onProcess={(data, closeModal) => onStartProcess(data, closeModal)}
                  rowTable={rowTable}
                  titleModal={CREATE_AI_METADATA}
                />
                <MainTotalItems
                  label={AI_METADATA_QUEUED_FOR_BUILDING_TITLE}
                  total={itemsResponse?.total}
                />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  className="mb-5 mt-5"
                >
                  <DownloadFileButton
                    service={(getAccessToken) => getAIMetadataBuildBatchItemsAsCsv({ id: batchId }, getAccessToken)}
                    isDisabled={!itemsResponse?.data?.length}
                    dataKey="csv"
                  >
                    { EXPORT_AI_METADATA_LABEL }
                  </DownloadFileButton>
                </Box>
                { isCompleted
                  ? (
                  <AIMetadataCompleteDataList
                    assetResponse={itemsResponse}
                    isHiddenForm={isHiddenForm}
                    onChangePage={onChangePage}
                    page={page}
                    totalPages={totalPages}
                  />
                    )
                  : (
                  <AIMetadataDataLists
                    assetResponse={itemsResponse}
                    batchName={aiMetadataBatch?.name}
                    batchHasErrors={batchHasErrors}
                    batchId={batchId}
                    isHiddenForm={isHiddenForm}
                    onChangePage={onChangePage}
                    page={page}
                    reloadItems={reloadAll}
                    totalPages={totalPages}
                    aiStrategies={aiStrategies?.embeddingModels}
                  />
                    )}
            </div>
          }
        </>
      }
    </RegularTemplate>
  )
}

export default AIMetadataBatch
